import { Password, UserMinus } from '@phosphor-icons/react'
import { useCan } from '@/auth/context'
import { Button, IconButton } from '@/ui'
import { cn } from '@/lib/cn'
import { teamRoleLabel } from '../model/deviceForm'

export interface TeamTableRow {
  userId: string
  name: string
  roles: string[]
  hasPin: boolean
}

const TH = 'border-b border-rule px-2 py-2 text-left font-mono text-[10.5px] font-bold uppercase tracking-eyebrow text-muted'
const TD = 'border-b border-rule/70 px-2 py-2.5 align-middle group-last:border-b-0'

/**
 * A phone team as one aligned table: person, role, PIN status, actions. Used on the phone drawer
 * and the site team so both read the same way. Actions hide without the permission.
 */
export function TeamTable<T extends TeamTableRow>({
  rows,
  onPin,
  onRemove,
  removePerm,
  compact = false,
}: {
  rows: T[]
  onPin: (row: T) => void
  /** Left out where people are removed elsewhere (a site team on the phone drawer). */
  onRemove?: (row: T) => void
  removePerm?: string
  /** Narrow places (a sidebar card): the role goes under the name and the actions are icons. */
  compact?: boolean
}) {
  const can = useCan()
  const canPin = can('pins:manage')
  const canRemove = Boolean(onRemove) && (!removePerm || can(removePerm))
  return (
    <div className="-mx-2 overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-[14px]">
        <thead>
          <tr>
            <th scope="col" className={TH}>
              Person
            </th>
            {!compact && (
              <th scope="col" className={TH}>
                Role
              </th>
            )}
            <th scope="col" className={TH}>
              PIN
            </th>
            {(canPin || canRemove) && (
              <th scope="col" className={cn(TH, 'text-right')}>
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.userId} className="group">
              <td className={cn(TD, 'text-ink')}>
                {m.name || 'Unnamed user'}
                {compact && <div className="text-[12.5px] text-muted">{roleText(m.roles)}</div>}
              </td>
              {!compact && (
                <td className={cn(TD, 'whitespace-nowrap text-[13px] text-ink-soft')}>{roleText(m.roles)}</td>
              )}
              <td className={cn(TD, 'whitespace-nowrap text-[13px]')}>
                <PinStatus hasPin={m.hasPin} />
              </td>
              {(canPin || canRemove) && (
                <td className={cn(TD, 'whitespace-nowrap text-right')}>
                  <span className="inline-flex items-center justify-end gap-1">
                    {canPin &&
                      (compact ? (
                        <IconButton
                          size="sm"
                          label={`${m.hasPin ? 'Change' : 'Set'} ${m.name || 'this person'}'s PIN`}
                          icon={<Password />}
                          onClick={() => onPin(m)}
                        />
                      ) : (
                        <Button size="sm" variant="ghost" leadingIcon={<Password />} onClick={() => onPin(m)}>
                          {m.hasPin ? 'Change PIN' : 'Set PIN'}
                        </Button>
                      ))}
                    {canRemove && (
                      <IconButton
                        size="sm"
                        label={`Remove ${m.name || 'this person'}`}
                        icon={<UserMinus />}
                        onClick={() => onRemove!(m)}
                      />
                    )}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const roleText = (roles: string[]) => (roles.length ? roles.map(teamRoleLabel).join(', ') : 'No team role')

function PinStatus({ hasPin }: { hasPin: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', hasPin ? 'text-ok' : 'text-warn')}>
      <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', hasPin ? 'bg-ok' : 'bg-warn')} />
      {hasPin ? 'Set' : 'Not set'}
    </span>
  )
}
